import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

export interface AccountActionProps {
  id: string;
  titleText: string;
  illustrationKey: IllustrationKeyType;
  tag?: TagProps;
  onPress?: () => void;
  disabled?: boolean;
}

const AccountActionItem = ({
  titleText,
  illustrationKey,
  tag,
  onPress,
  disabled,
}: AccountActionProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onBlur={() => setIsFocused(false)}
      onFocus={() => setIsFocused(true)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={disabled}
      style={[
        styles.container,
        isFocused && interactiveStyles.focus,
        isHovered && interactiveStyles.hover,
        isPressed && interactiveStyles.press,
      ]}
    >
      <View style={styles.contentContainer}>
        <View style={styles.leftSide}>
          <View style={styles.iconContainer}>
            <Illustration illustration={illustrationKey} />
          </View>
          <BodySmText
            style={[styles.text, disabled && interactiveStyles.textDisable]}
            numberOfLines={0}
          >
            {titleText}
          </BodySmText>
        </View>
        {tag ? (
          <View style={styles.tagContainer}>
            <Tag {...tag} />
          </View>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.interactive.tertiary,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: 12,
  },
  contentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  iconContainer: {
    alignItems: 'center',
    height: theme.spacing.xl,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xs,
    width: theme.spacing.xl,
  },
  leftSide: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    gap: theme.spacing.s,
  },
  tagContainer: {
    marginLeft: theme.spacing.s,
  },
  text: {
    flex: 1,
  },
});

const interactiveStyles = StyleSheet.create({
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.tertiaryHovered,
  },
  press: {
    backgroundColor: theme.color.interactive.tertiaryPressed,
  },
  textDisable: {
    color: theme.color.interactive.disabled.text,
  },
});

export default AccountActionItem;
